/**
 * Canon
 *
 * @package     mcb-front-end
 * @project     Ignite
 * @desc        Main authentication thru STS. Uses code from auth0.com
 *
 */

// -------------------------------------
//   Dependencies: core
// -------------------------------------
import auth0 from 'auth0-js'
import axios from 'axios'
import storage from 'localforage'

import * as actionCreators from 'redux/actions/index.js'
import { store, persistor } from '../redux/store.js'

import PreAuth from './preAuth'
import PostAuth from './postAuth'

import {
	clearIdToken,
	clearAccessToken,
	getIdToken,
	isTokenExpired,
	clearSelectedAndFavAccounts
} from './tokenHandlers'
import { deleteCallBack } from './callBackHandlers'
import { clearPermissions } from './aclHandlers'
import {
	CLIENT_ID,
	CLIENT_DOMAIN,
	ISPROD,
	CORE_URL,
	SCOPE,
	REDIRECT,
	OPENID_CONNECT_MULTIVALUES_RESPONSE_TYPE,
	AUTOSIGN_COOKIE,
	ACR_SFDC,
	RELOGIN_FLAG,
	SELECTED_ACCOUNT,
	CALLBACK
} from './constants'
import {
	SIGN_IN_IDP,
	SIGN_IN_FAILURES,
	newSignInReference,
	readSignInFailure,
	rememberedIdp,
	signInFailurePath
} from './signInFailure'

var auth = new auth0.WebAuth({
	clientID: CLIENT_ID,
	domain: CLIENT_DOMAIN,
	overrides: {
		__tenant: ISPROD ? 'secure' : 'secure',
		__token_issuer: CORE_URL,
		__jwks_uri: `${CORE_URL}/.well-known/jwks`
	}
})

// idtoken-verifier 2.2.4 words its expiry and not-before failures "Expiration Time (exp) claim error ..." and
// "Not Before Time (nbf) claim error ...": a client clock off by more than the 60 s leeway
const CLOCK_ERROR = /\((exp|nbf)\) claim error/
// a hung IndexedDB must not keep the user on the callback page
const LEAVE_AFTER_MS = 2000

let callbackHandled = false
let leavingFailedCallback = false

const quietly = fn => {
	try {
		return fn()
	} catch (e) {
		return undefined
	}
}

// same-origin paths only: the stored values come from URL paths, and '//' or '/\' would leave the site
const sameOriginPath = path => (typeof path === 'string' && /^\/(?![/\\])/.test(path) ? path : null)

//[mh] INC1465998: builds before 3.31.0 (vendored auth0.js 9.5.1), still served from browser caches, keep the login
// transaction in localStorage, while auth0-js 10 looks only in its cookie, so a login they started failed the state
// check here. Remove once the STS logs show no /authorize with auth0Client version 9.5.1.
const legacyTransaction = () =>
	quietly(() => {
		const state = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('state')
		if (!state) return undefined
		const key = `com.auth0.auth.${state}`
		const stored = JSON.parse(localStorage.getItem(key))
		localStorage.removeItem(key)
		// without a nonce the verifier would skip the nonce check
		return stored && stored.state === state && typeof stored.nonce === 'string' && stored.nonce
			? { state: stored.state, nonce: stored.nonce }
			: undefined
	})

// ---------------------------------------------
//   Authentication handlers
// ---------------------------------------------
/**
 * @name login
 * @desc Redirects user to federated login (STS in our case)
 */
export function login(idp, isReLogin = false) {
	//[mh] INC1465998: a failed callback is reloading the page; a 401 from a request it already started routes
	// to ReLogin, which must not start a second login with the STS session still alive
	if (leavingFailedCallback) return
	//[mh] INC1465998: every Login button on the failed sign-in notice page retries with the failed attempt's idp
	if (idp === undefined && quietly(readSignInFailure)) idp = rememberedIdp()
	//[sp] invoke the PreAuth Process to clear tokens and purge peristed redux state and set IsAuthenticating flag to true
	PreAuth.process(isReLogin)
	//[mh] INC1465998: kept for this tab so a retry after a failed callback keeps the Salesforce hint
	quietly(() => sessionStorage.setItem(SIGN_IN_IDP, idp || ''))
	auth.authorize({
		responseType: OPENID_CONNECT_MULTIVALUES_RESPONSE_TYPE,
		redirectUri: REDIRECT,
		scope: SCOPE,
		acr_values: idp ? ACR_SFDC : ''
	})
	if (window.DEBUG) console.log('logn: login', auth)
	//sri - The whole process of authetication has started so set the auth.isAuthenticating state to false.
}
/**
 * @name handleAuthentication
 * @desc Checks the returned package from STS against the sent NONCE and STATE
 */
export function handleAuthentication() {
	//[mh] INC1465998: the callback route calls this from its render prop, and the login transaction can be read once
	if (callbackHandled) return
	callbackHandled = true
	let settled = false
	auth.parseHash(legacyTransaction() || {}, (err, authResult) => {
		if (window.DEBUG) console.log('auth: handle', authResult)
		//[mh] INC1465998: idtoken-verifier runs this callback inside a promise whose catch calls it again with any
		// exception the first call threw, so a post-login exception arrived here as a token error and logged the user
		// out of STS. MCB's own try/catch below stops that, and anything that still calls back twice is reported.
		if (settled) {
			if (err) reportSignInError(err, 'repeat-callback')
			return
		}
		settled = true
		if (authResult && authResult.accessToken && authResult.idToken) {
			try {
				PostAuth.process(authResult)
				quietly(() => sessionStorage.removeItem(SIGN_IN_FAILURES))
			} catch (e) {
				recoverFromFailedCallback(e, 'post-auth')
			}
		} else {
			// an error from STS or from token validation, or a callback URL without tokens
			recoverFromFailedCallback(err || new Error('The sign-in callback had no tokens'), err ? 'parse' : 'empty')
		}
	})
}

/**
 * @name reportSignInError
 * @desc Console and New Relic (when the agent is on the page); never the tokens or the hash
 */
function reportSignInError(error, stage, details = {}) {
	console.error(error)
	quietly(() => {
		if (!window.newrelic || typeof window.newrelic.noticeError !== 'function') return
		// auth0 errors are {error, errorDescription}; the app's ErrorCodes objects are {Name, Code, message()}
		const message = error && typeof error.message === 'string' ? error.message : ''
		const description = String((error && error.errorDescription) || message || (error && error.Name) || error).slice(0, 200)
		const code = String((error && (error.error || error.Name || error.name)) || 'unknown').replace(/[^\w.-]/g, '').slice(0, 64)
		// a fixed message keeps New Relic grouping by stage; the exception itself is passed when there is one (stack)
		window.newrelic.noticeError(error instanceof Error ? error : new Error(`Sign-in failed (${stage})`), {
			signInStage: stage,
			signInError: code,
			signInDescription: description,
			appVersion: process.env.REACT_APP_VERSION,
			...details
		})
	})
}

/**
 * @name recoverFromFailedCallback
 * @desc INC1465998: leaves the callback without ending the STS session, so a retry needs no password or 2FA
 */
function recoverFromFailedCallback(error, stage) {
	leavingFailedCallback = true
	const deepLink = quietly(() => localStorage.getItem(CALLBACK))

	if (quietly(isLoggedIn)) {
		// a session is already stored (the callback page was reloaded after signing in, or another tab signed in):
		// not a failed sign-in, so it is neither counted nor reported
		console.error(error)
		const account = quietly(() => localStorage.getItem(SELECTED_ACCOUNT))
		const accountPath = typeof account === 'string' && /^[A-Za-z0-9-]+$/.test(account) ? `/${account}` : null
		window.location.replace(
			(deepLink !== 'pending' && sameOriginPath(deepLink)) || accountPath || '/'
		)
		return
	}

	const reference = newSignInReference()
	const attempt =
		quietly(() => {
			const count = Number(sessionStorage.getItem(SIGN_IN_FAILURES) || 0) + 1
			sessionStorage.setItem(SIGN_IN_FAILURES, String(count))
			return count
		}) || 1
	reportSignInError(error, stage, { signInAttempt: attempt, signInReference: reference })

	// a 401 from a request this page already started lands on /accesserror, whose ReLogin stores that path as the
	// deep link (canCallBack) until the page unloads; undo only that write, so another tab's deep link survives
	const keepDeepLink = () =>
		quietly(() => {
			const path = window.location.pathname
			if (path === '/callback' || localStorage.getItem(CALLBACK) !== path) return
			if (deepLink) localStorage.setItem(CALLBACK, deepLink)
			else localStorage.removeItem(CALLBACK)
		})
	quietly(() => window.addEventListener('pagehide', keepDeepLink, { once: true }))
	let left = false
	const leave = () => {
		if (left) return
		left = true
		keepDeepLink()
		window.location.replace(signInFailurePath(reference, CLOCK_ERROR.test(String(error && error.errorDescription))))
	}
	setTimeout(leave, LEAVE_AFTER_MS)
	// drop the persisted redux state so the retry does not rehydrate whatever broke this sign-in; pause and flush
	// first, because a write redux-persist has already staged would otherwise put it back after the clear
	Promise.resolve()
		.then(() => {
			persistor.pause()
			return persistor.flush()
		})
		.then(() => storage.clear())
		.catch(e => console.error(e))
		.then(leave)
}

/**
 * @name logout
 * @desc Clear localStorage and logout session thru STS
 */
export function logout() {
	store.dispatch(actionCreators.setAuth({}))
	clearIdToken()
	clearAccessToken()
	clearPermissions()
	clearSelectedAndFavAccounts()
	// history.replace('/')
	persistor.purge()
	deleteCallBack()
	clearLSAuth()
	//To remove the cookie ,expire it to the
	document.cookie = AUTOSIGN_COOKIE + '=; expires=Thu, 01 Jan 1970 00:00:01 GMT; domain=.canon.com.au; path=/'
	const redirect =
		'https://' +
		CLIENT_DOMAIN +
		'/endsession?post_logout_redirect_uri=' +
		window.location.origin +
		'&client_id=' +
		CLIENT_ID
	window.location.href = encodeURI(redirect)
}

export function reLoginSSO(accountNumber) {
	store.dispatch(actionCreators.setAuth({}))
	clearIdToken()
	clearAccessToken()
	clearPermissions()
	
	persistor.purge()
	deleteCallBack()
	clearLSAuth()
	//To remove the cookie ,expire it to the
	document.cookie = AUTOSIGN_COOKIE + '=; expires=Thu, 01 Jan 1970 00:00:01 GMT; domain=.canon.com.au; path=/'

	const redirect =
		'https://' +
		CLIENT_DOMAIN +
		'/endsession?post_logout_redirect_uri=' +
		window.location.origin +
		'&client_id=' +
		CLIENT_ID

	//call redirect as AJAX to get session logout in STS
	axios
    .get(redirect)
    .then(() => {
			login(ACR_SFDC, true)
    })
    .catch(error => {
        if (window.DEBUG) console.timeEnd(redirect)
				if (window.DEBUG) console.log(error)

				const redirect =
						'https://' +
						CLIENT_DOMAIN +
						'/endsession?post_logout_redirect_uri=' +
						window.location.origin + "/" + accountNumber + "?idp=SFDC" +
						'&client_id=' +
						CLIENT_ID
				
				
					window.location.href = encodeURI(redirect)
    })	

	
	
}

/**
 * @name clearLSAuth
 * @desc Clears localStorage associated with the 3rd partys auth0.com
 */
export function clearLSAuth() {
	for (var key in localStorage) {
		if (key.indexOf('com.auth0') === 0) {
			localStorage.removeItem(key)
		}
	}
}
/**
 * @name changePassword
 * @desc Redirect user to STS change password
 */
export function changePassword() {
	const sub = localStorage.getItem('sub')
	if (sub) {
		let redirect =
			'https://' + CLIENT_DOMAIN + '/password/change?returnUrl=' + window.location.href + '&userid=' + sub
		redirect = redirect.replace('/connect', '')
		window.location.href = encodeURI(redirect)
	}
}

export function autoSignInOnLand() {
	const idToken = getIdToken()
	const isTokenExp = isTokenExpired(idToken)
	//[sp] If token is expired / or if not present check for the autosign cookie. If present try doing a autosign in by calling the login method
	if (isTokenExp) {
		if (document.cookie.indexOf(AUTOSIGN_COOKIE) > -1) {
			//do a autologin , since the STS would be holding the XVRF Session token to auto authenticate the request
			// login()
		}
	}
}
/**
 * @name requireAuth
 * @desc
 */
export function requireAuth(nextState, replace) {
	if (!isLoggedIn()) {
		replace({ pathname: '/' })
	}
}
/**
 * @name isLoggedIn
 * @desc
 */
export function isLoggedIn() {
	const idToken = getIdToken()
	const isTokenExp = isTokenExpired(idToken)

	return !!idToken && !isTokenExp
}
